import { logError, logInformation, logWarning } from '@core/logging';
import { useEffect, useState } from 'react';
import { serializeError } from 'serialize-error';

export function useAudioStream() {
    const [audioDeviceId, setAudioDeviceId] = useState<string | null>(null);
    const [audioQuality, setAudioQuality] = useState<string | null>(null);
    const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
    const [hasAudioError, setHasAudioError] = useState(false);

    useEffect(() => {
        if (!audioDeviceId || audioDeviceId === 'rtmp' || audioDeviceId === 'srt') {
            setAudioStream(null);
            setAudioQuality(null);
            setHasAudioError(false);
            return;
        }

        let stream: MediaStream | null = null;
        const getAudioStream = async () => {
            const streamPriority: { description: string; constraints: MediaTrackConstraints }[] = [
                {
                    description: 'High',
                    constraints: {
                        deviceId: { exact: audioDeviceId || '' },
                        channelCount: { ideal: 2 },
                        autoGainControl: { ideal: false },
                        echoCancellation: { ideal: false },
                        noiseSuppression: { ideal: false },
                        sampleRate: { exact: 96000 },
                        sampleSize: { exact: 16 }
                    }
                },
                {
                    description: 'Normal',
                    constraints: {
                        deviceId: { exact: audioDeviceId || '' },
                        channelCount: { ideal: 2 },
                        autoGainControl: { ideal: false },
                        echoCancellation: { ideal: false },
                        noiseSuppression: { ideal: false },
                        sampleRate: { exact: 48000 },
                        sampleSize: { exact: 16 }
                    }
                },
                {
                    description: 'Low',
                    constraints: {
                        deviceId: { exact: audioDeviceId || '' },
                        channelCount: { ideal: 2 },
                        autoGainControl: { ideal: false },
                        echoCancellation: { ideal: false },
                        noiseSuppression: { ideal: false },
                        sampleRate: { ideal: 48000 },
                        sampleSize: { ideal: 16 }
                    }
                }
            ];

            logInformation('Retrieving audio stream for {AudioDevice}', { AudioDevice: audioDeviceId });
            for (const { description, constraints } of streamPriority) {
                try {
                    stream = await navigator.mediaDevices.getUserMedia({ audio: constraints });

                    const settings = stream.getAudioTracks()[0].getSettings();
                    const capabilities = stream.getAudioTracks()[0].getCapabilities();
                    logInformation('Successfully obtained {Description} quality audio stream', {
                        Settings: settings,
                        Description: description,
                        Constraints: constraints,
                        Capabilities: capabilities
                    });

                    if (settings.sampleRate) {
                        setAudioQuality(`${settings.sampleRate}Hz @ ${settings.channelCount}ch`);
                    } else {
                        setAudioQuality(description);
                    }
                    break;
                } catch (e) {
                    logWarning('Failed to get {Description} audio stream ({Error})', {
                        Error: serializeError(e),
                        Description: description,
                        Constraints: constraints
                    });
                }
            }

            if (!stream) {
                logError('Failed to get audio stream');
                setAudioQuality('Failed to get audio stream');
            }

            setHasAudioError(!stream);
            setAudioStream(stream);
        };

        getAudioStream();
        return () => {
            if (stream) {
                stream.getTracks().forEach((track) => track.stop());
            }
        };
    }, [audioDeviceId]);

    return { audioStream, hasAudioError, setAudioDeviceId, audioQuality };
}
